import { MemoizedSelector, ReducerTypes, createSelector, on, props } from '@ngrx/store';
import { ModalRef } from '@core-lib/modules/modal-container';
import { createTypedAction } from './action-creators.function';
import { CorrelationParams, newAggregationId } from './correlated-actions.model';

/**
 * ========================================================================
 * Entity slice creation helpers
 * Source: https://github.com/vincent/ng-stator
 * ========================================================================
 */

/**
 * State Model
 */
export interface IStoreVoidEntityStateSlice {
	isLoading: boolean;
	error: any;
}

export interface IStoreEntityStateSlice<Entity> {
	isLoading: boolean;
	error: any;
	entity: Entity;
}

export function createEntitySlice<Entity>(defaults?: Partial<IStoreEntityStateSlice<Entity>>): IStoreEntityStateSlice<Entity> {
	return {
		entity: null,
		isLoading: false,
		error: null,
		...defaults
	};
}

export function createVoidEntitySlice(defaults?: Partial<IStoreVoidEntityStateSlice>): IStoreVoidEntityStateSlice {
	return {
		isLoading: false,
		error: null,
		...defaults
	};
}

/**
 * Entity Action Types
 */
export enum EntityActionType {
	LoadEntityByIdRequest = 'LoadEntityByIdRequest',
	LoadEntityRequest = 'LoadEntityRequest',
	LoadEntitySuccess = 'LoadEntitySuccess',
	LoadEntityMergeSuccess = 'LoadEntitySuccess',
	LoadEntityFailure = 'LoadEntityFailure',

	CreateEntityConfirm = 'CreateEntityConfirm',
	CreateEntityRequest = 'CreateEntityRequest',
	CreateEntitySuccess = 'CreateEntitySuccess',
	CreateEntityFailure = 'CreateEntityFailure',

	UpdateEntityConfirm = 'UpdateEntityConfirm',
	UpdateEntityRequest = 'UpdateEntityRequest',
	UpdateEntitySuccess = 'UpdateEntitySuccess',
	UpdateEntityFailure = 'UpdateEntityFailure',

	PreviewEntityRequest = 'PreviewEntityRequest',
	PreviewEntitySuccess = 'PreviewEntitySuccess',
	PreviewEntityFailure = 'PreviewEntityFailure',

	DeleteEntityConfirm = 'DeleteEntityConfirm',
	DeleteEntityRequest = 'DeleteEntityRequest',
	DeleteEntityByIdRequest = 'DeleteEntityByIdRequest',
	DeleteEntitySuccess = 'DeleteEntitySuccess',
	DeleteEntityFailure = 'DeleteEntityFailure',

	SendEntityConfirm = 'SendEntityConfirm',
	SendEntityRequest = 'SendEntityRequest',
	SendEntitySuccess = 'SendEntitySuccess',
	SendEntityFailure = 'SendEntityFailure',

	SubmitEntityConfirm = 'SubmitEntityConfirm',
	SubmitEntityRequest = 'SubmitEntityRequest',
	SubmitEntitySuccess = 'SubmitEntitySuccess',
	SubmitEntityFailure = 'SubmitEntityFailure',

	SetEntityModal = 'SetEntityModal',
	CloseEntityModal = 'CloseEntityModal'
}

export function createEntityActions<Entity>(storeName: string, entityName: string) {
	return {
		LoadEntityRequest: createTypedAction(
			EntityActionType.LoadEntityRequest,
			`[${storeName}] Load ${entityName} Request`,
			(
				correlationParams: CorrelationParams = {
					correlationId: newAggregationId()
				}
			) => ({ correlationParams })
		),
		LoadEntityByIdRequest: createTypedAction(
			EntityActionType.LoadEntityRequest,
			`[${storeName}] Load ${entityName} By ID Request`,
			(
				id: number | string,
				correlationParams: CorrelationParams = {
					correlationId: newAggregationId()
				}
			) => ({
				id,
				correlationParams
			})
		),
		LoadEntitySuccess: createTypedAction(
			EntityActionType.LoadEntitySuccess,
			`[${storeName}] Load ${entityName} Success`,
			props<{ entity: Entity; correlationParams?: CorrelationParams }>()
		),
		LoadEntityMergeSuccess: createTypedAction(
			EntityActionType.LoadEntityMergeSuccess,
			`[${storeName}] Load ${entityName} Merge Success`,
			props<{ entity: Entity; correlationParams?: CorrelationParams }>()
		),
		LoadVoidEntitySuccess: createTypedAction(
			EntityActionType.LoadEntitySuccess,
			`[${storeName}] Load ${entityName} Success`,
			props<{ correlationParams?: CorrelationParams }>()
		),
		LoadEntityFailure: createTypedAction(
			EntityActionType.LoadEntityFailure,
			`[${storeName}] Load ${entityName} Failure`,
			props<{ error: Error }>()
		),
		CreateEntityConfirm: createTypedAction(
			EntityActionType.CreateEntityConfirm,
			`[${storeName}] Create ${entityName} Confirm`,
			props<{ entity: Entity; isDirty?: boolean }>()
		),
		CreateEntityRequest: createTypedAction(
			EntityActionType.CreateEntityRequest,
			`[${storeName}] Create ${entityName} Request`,
			(
				entity: Entity,
				correlationParams: CorrelationParams = {
					correlationId: newAggregationId()
				}
			) => ({
				entity,
				correlationParams
			})
		),
		CreateEntitySuccess: createTypedAction(
			EntityActionType.CreateEntitySuccess,
			`[${storeName}] Create ${entityName} Success`,
			props<{ entity: Entity; correlationParams?: CorrelationParams }>()
		),
		CreateEntityFailure: createTypedAction(
			EntityActionType.CreateEntityFailure,
			`[${storeName}] Create ${entityName} Failure`,
			props<{ error: Error }>()
		),
		UpdateEntityConfirm: createTypedAction(
			EntityActionType.UpdateEntityConfirm,
			`[${storeName}] Update ${entityName} Confirm`,
			props<{ entity: Entity; isDirty?: boolean }>()
		),
		UpdateEntityRequest: createTypedAction(
			EntityActionType.UpdateEntityRequest,
			`[${storeName}] Update ${entityName} Request`,
			(
				entity: Entity,
				correlationParams: CorrelationParams = {
					correlationId: newAggregationId()
				}
			) => ({
				entity,
				correlationParams
			})
		),
		UpdateEntitySuccess: createTypedAction(
			EntityActionType.UpdateEntitySuccess,
			`[${storeName}] Update ${entityName} Success`,
			props<{ entity: Entity; correlationParams?: CorrelationParams }>()
		),
		UpdateEntityFailure: createTypedAction(
			EntityActionType.UpdateEntityFailure,
			`[${storeName}] Update ${entityName} Failure`,
			props<{ error: Error }>()
		),
		SendEntitySuccess: createTypedAction(
			EntityActionType.UpdateEntitySuccess,
			`[${storeName}] Update ${entityName} Success`,
			props<{ entity: Entity; correlationParams?: CorrelationParams }>()
		),
		SendEntityFailure: createTypedAction(
			EntityActionType.UpdateEntityFailure,
			`[${storeName}] Update ${entityName} Failure`,
			props<{ error: Error }>()
		),
		SubmitEntitySuccess: createTypedAction(
			EntityActionType.UpdateEntitySuccess,
			`[${storeName}] Update ${entityName} Success`,
			props<{ entity: Entity; correlationParams?: CorrelationParams }>()
		),
		SubmitEntityFailure: createTypedAction(
			EntityActionType.UpdateEntityFailure,
			`[${storeName}] Update ${entityName} Failure`,
			props<{ error: Error }>()
		),
		PreviewEntityRequest: createTypedAction(
			EntityActionType.PreviewEntityRequest,
			`[${storeName}] Preview ${entityName} Request`,
			(
				entity?: Entity,
				correlationParams: CorrelationParams = {
					correlationId: newAggregationId()
				},
				isCommunReceptionMode?: boolean
			) => ({
				entity,
				correlationParams,
				isCommunReceptionMode
			})
		),
		PreviewEntitySuccess: createTypedAction(
			EntityActionType.PreviewEntitySuccess,
			`[${storeName}] Preview ${entityName} Success`,
			props<{ entity: Entity; correlationParams?: CorrelationParams }>()
		),
		PreviewEntityFailure: createTypedAction(
			EntityActionType.PreviewEntityFailure,
			`[${storeName}] Preview ${entityName} Failure`,
			props<{ error: Error }>()
		),
		DeleteEntityConfirm: createTypedAction(
			EntityActionType.DeleteEntityConfirm,
			`[${storeName}] Delete ${entityName} Confirm`,
			props<{
				entity?: Entity;
				isDirty?: boolean;
				shouldRedirect?: boolean;
			}>()
		),
		DeleteEntityRequest: createTypedAction(
			EntityActionType.DeleteEntityRequest,
			`[${storeName}] Delete ${entityName} Request`,
			(
				entity?: Entity,
				correlationParams: CorrelationParams = {
					correlationId: newAggregationId()
				},
				shouldRedirect?: boolean
			) => ({
				entity,
				correlationParams,
				shouldRedirect
			})
		),
		DeleteEntityByIdRequest: createTypedAction(
			EntityActionType.DeleteEntityByIdRequest,
			`[${storeName}] Delete ${entityName} By ID Request`,
			(
				id: number | string,
				correlationParams: CorrelationParams = {
					correlationId: newAggregationId()
				},
				shouldRedirect?: boolean
			) => ({
				id,
				correlationParams,
				shouldRedirect
			})
		),
		DeleteEntitySuccess: createTypedAction(
			EntityActionType.DeleteEntitySuccess,
			`[${storeName}] Delete ${entityName} Success`,
			props<{
				entity?: Entity;
				correlationParams?: CorrelationParams;
				shouldRedirect?: boolean;
			}>()
		),
		DeleteEntityFailure: createTypedAction(
			EntityActionType.DeleteEntityFailure,
			`[${storeName}] Delete ${entityName} Failure`,
			props<{ error: Error }>()
		),
		SetEntityModal: createTypedAction(
			EntityActionType.SetEntityModal,
			`[${storeName}] Set ${entityName} Modal`,
			props<{ modal: ModalRef['id'] }>()
		),
		CloseEntityModal: createTypedAction(EntityActionType.CloseEntityModal, `[${storeName}] Close ${entityName} Modal`)
	};
}

export function createEntityActionsBy<Entity, By>(storeName: string, entityName: string) {
	return {
		...createEntityActions<Entity>(storeName, entityName),
		UpdateEntityRequest: createTypedAction(
			EntityActionType.UpdateEntityRequest,
			`[${storeName}] Update ${entityName} Request`,
			(
				entity: By,
				correlationParams: CorrelationParams = {
					correlationId: newAggregationId()
				}
			) => ({
				entity,
				correlationParams
			})
		)
	};
}

export function entitySliceRequestReducer<S extends object>(state: S, slice: keyof S) {
	return {
		...state,
		[slice]: {
			...state[slice],
			isLoading: true,
			error: false
		}
	};
}

export function entitySliceSuccessReducer<S extends object, A extends { entity: any }>(state: S, slice: keyof S, action?: A) {
	return {
		...state,
		[slice]: {
			...state[slice],
			isLoading: false,
			error: false,
			entity: action ? action.entity : state[slice]['entity']
		}
	};
}

export function entitySliceMergeSuccessReducer<S extends object, A extends { entity: any }>(state: S, slice: keyof S, action?: A) {
	return {
		...state,
		[slice]: {
			...state[slice],
			isLoading: false,
			error: false,
			entity: {
				...state[slice]['entity'],
				...action.entity
			}
		}
	};
}

export function entitySliceFailureReducer<S extends object, A extends { error: any }>(state: S, slice: keyof S, action: A) {
	return {
		...state,
		[slice]: {
			...state[slice],
			isLoading: false,
			error: action.error
		}
	};
}

export function entitySliceSetModalReducer<S extends object, A extends { modal: ModalRef['id'] }>(state: S, slice: keyof S, action: A) {
	return {
		...state,
		[`${slice as string} Modal`]: action.modal
	};
}

export function entitySliceCloseModalReducer<S extends object>(state: S, slice: keyof S) {
	return {
		...state,
		[`${slice as string} Modal`]: null
	};
}

/**
 * Reducers
 */
export function withEntityReducer<S extends object>(
	slice: keyof S,
	{
		LoadEntityByIdRequest,
		LoadEntityRequest,
		CreateEntityRequest,
		UpdateEntityRequest,
		LoadEntitySuccess,
		DeleteEntityRequest,
		DeleteEntityByIdRequest,
		LoadEntityMergeSuccess,
		CreateEntitySuccess,
		UpdateEntitySuccess,
		DeleteEntitySuccess,
		LoadEntityFailure,
		CreateEntityFailure,
		UpdateEntityFailure,
		PreviewEntityRequest,
		PreviewEntitySuccess,
		PreviewEntityFailure,
		DeleteEntityFailure,
		SetEntityModal,
		CloseEntityModal
	}
): ReducerTypes<any, any>[] {
	return [
		on(LoadEntityRequest, (state: S, action) => entitySliceRequestReducer(state, slice)),
		on(CreateEntityRequest, (state: S, action) => entitySliceRequestReducer(state, slice)),
		on(UpdateEntityRequest, (state: S, action) => entitySliceRequestReducer(state, slice)),
		on(LoadEntityByIdRequest, (state: S, action) => entitySliceRequestReducer(state, slice)),
		on(PreviewEntityRequest, (state: S, action) => entitySliceRequestReducer(state, slice)),
		on(DeleteEntityRequest, (state: S, action) => entitySliceRequestReducer(state, slice)),
		on(DeleteEntityByIdRequest, (state: S, action) => entitySliceRequestReducer(state, slice)),

		on(LoadEntitySuccess, <A extends { entity: any }>(state: S, action: A) => entitySliceSuccessReducer(state, slice, action)),
		on(LoadEntityMergeSuccess, <A extends { entity: any }>(state: S, action: A) =>
			entitySliceMergeSuccessReducer(state, slice, action)
		),
		on(CreateEntitySuccess, <A extends { entity: any }>(state: S, action: A) => entitySliceSuccessReducer(state, slice, action)),
		on(UpdateEntitySuccess, <A extends { entity: any }>(state: S, action: A) => entitySliceSuccessReducer(state, slice, action)),
		on(PreviewEntitySuccess, <A extends { entity: any }>(state: S, action: A) => entitySliceSuccessReducer(state, slice, action)),
		on(DeleteEntitySuccess, <A extends { entity: any }>(state: S, action: A) => entitySliceSuccessReducer(state, slice, action)),

		on(LoadEntityFailure, <A extends { error: any }>(state: S, action: A) => entitySliceFailureReducer(state, slice, action)),
		on(CreateEntityFailure, <A extends { error: any }>(state: S, action: A) => entitySliceFailureReducer(state, slice, action)),
		on(UpdateEntityFailure, <A extends { error: any }>(state: S, action: A) => entitySliceFailureReducer(state, slice, action)),
		on(PreviewEntityFailure, <A extends { error: any }>(state: S, action: A) => entitySliceFailureReducer(state, slice, action)),
		on(DeleteEntityFailure, <A extends { error: any }>(state: S, action: A) => entitySliceFailureReducer(state, slice, action)),

		on(SetEntityModal, <A extends { modal: ModalRef['id'] }>(state: S, action: A) =>
			entitySliceSetModalReducer(state, slice, action)
		),
		on(CloseEntityModal, (state: S, action) => entitySliceCloseModalReducer(state, slice))
	];
}

/**
 * Selectors
 */
export interface IStoreEntityStateSelectors<Entity> {
	getError: (state: IStoreEntityStateSlice<Entity>) => any;
	selectError: MemoizedSelector<object, any>;

	getIsLoading: (state: IStoreEntityStateSlice<Entity>) => boolean;
	selectIsLoading: MemoizedSelector<object, boolean>;

	getEntity: (state: IStoreEntityStateSlice<Entity>) => Entity;
	selectEntity: MemoizedSelector<object, Entity>;
}

export function createEntitySliceSelectors<Entity>(
	sliceSelector: MemoizedSelector<object, IStoreVoidEntityStateSlice | IStoreEntityStateSlice<Entity>>
): IStoreEntityStateSelectors<Entity> {
	const getError = (state: IStoreEntityStateSlice<Entity>): any => state.error;
	const selectError: MemoizedSelector<object, any> = createSelector(sliceSelector, getError);

	const getIsLoading = (state: IStoreEntityStateSlice<Entity>): boolean => state.isLoading;
	const selectIsLoading: MemoizedSelector<object, boolean> = createSelector(sliceSelector, getIsLoading);

	const getEntity = (state: IStoreEntityStateSlice<Entity>): Entity => state?.entity;
	const selectEntity: MemoizedSelector<object, Entity> = createSelector(sliceSelector, getEntity);

	return {
		getEntity,
		selectEntity,
		getError,
		selectError,
		getIsLoading,
		selectIsLoading
	};
}
